import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import LocationAutocomplete from './LocationAutocomplete';
import PhoneNumberInput from './PhoneNumberInput';
import Popup from './Popup';
import ClearableInput from './ClearableInput';
import '../styles/editPanel.css';
import CoverLetterGen from './CoverLetterGen';
import { useFirebaseAuth } from '../../contexts/FirebaseAuthProvider';
import { FaCloudUploadAlt } from 'react-icons/fa';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const CharacterCount = ({ current, max }) => {
  if (current < max) {
    return null;
  }
  return (
    <div className="character-count warning">
      {current}/{max} characters
    </div>
  );
};

const EditPanel = ({
  name = '',
  title = '',
  location = '',
  email = '',
  phone = '',
  summary = '',
  experience = '',
  skills = '',
  education = '',
  publications = '',
  // Cover letter related fields
  coverLetter = '',
  jobDescription = '',
  jobPostUrl = '',
  companyName = '',
  targetRole = '',
  applicantAddress = '',
  recipientName = '',
  recipientJobTitle = '',
  coverLetterBody = '',
  whyCompany = '',
  keyAchievements = '',
  keyRoleSkills = '',
  jobPreview = null,
  onInputChange,
  onGenerateClick,
  isLoading = false,
  statusMessage = '',
  characterLimits = {},
  activeSection = '',
  toggleSection,
  user,
  tier,
  credits,
  onShowCreditGate,
  handleCoverLetterGenerate,
  setActiveSection: setActiveSectionProp,
  fetchSubscription
}) => {
  const navigate = useNavigate();
  const { getToken } = useFirebaseAuth();
  const fileInputRef = useRef(null);
  const [isImporting, setIsImporting] = useState(false);

  const extractTextFromPdf = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
    const pdf = await loadingTask.promise;
    let fullText = '';
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = textContent.items.map(item => item.str).join(' ');
      fullText += pageText + '\n';
    }
    return fullText;
  };

  const handleImportClick = () => {
    if (!user) {
      alert("Please sign in or register to use the AI Document Upload feature.");
      navigate('/sign-in');
      return;
    }
    const remaining = credits?.remainingCredits || 0;
    if (remaining < 5) {
      if (typeof onShowCreditGate === 'function') {
        onShowCreditGate('ai_insufficient');
      } else {
        alert("Insufficient credits. You need at least 5 credits to use the AI Document Upload feature.");
      }
      return;
    }
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    try {
      let text = '';
      if (file.type === 'text/plain' || file.name.endsWith('.txt')) {
        text = await file.text();
      } else if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
        text = await extractTextFromPdf(file);
      } else {
        alert('Unsupported file type. Please upload a PDF or TXT file.');
        setIsImporting(false);
        return;
      }

      if (!text.trim()) {
        alert('Could not extract any text from the document. Please ensure it is not scanned or empty.');
        setIsImporting(false);
        return;
      }

      const token = await getToken();
      const response = await fetch('/api/ai/gemini/parse-resume', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ resumeText: text })
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Failed to parse resume');
      }

      const data = await response.json();
      if (data.success && data.parsedData) {
        const parsed = data.parsedData;
        Object.keys(parsed).forEach(key => {
          if (parsed[key] !== undefined && parsed[key] !== null) {
            onInputChange({ target: { name: key, value: parsed[key] } });
          }
        });
        alert('Resume imported and parsed successfully!');
        if (fetchSubscription) {
          fetchSubscription();
        }
      } else {
        throw new Error('Invalid parsing response');
      }
    } catch (err) {
      console.error(err);
      alert('Error parsing resume: ' + err.message);
    } finally {
      setIsImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const hasCoverLetterAccess = user && (tier === 'professional' || tier === 'ultimate');
  const [activeField, setActiveField] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const inputRefs = useRef({});
  const sectionRefs = useRef({});
  const panelRef = useRef(null);
  const [closingSection, setClosingSection] = useState(null);
  // const panelRef = useRef(null);

  const handleFocus = useCallback((name) => {
    setActiveField(name);
  }, []);

  const handleBlur = useCallback(() => {
    setActiveField(null);
  }, []);



  const getTextWidth = (text, font) => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    context.font = font || '16px Arial';
    return context.measureText(text).width;
  };

  const calculateInputWidth = useCallback((inputElement) => {
    const text = inputElement.value || inputElement.placeholder;
    const style = window.getComputedStyle(inputElement);
    const font = `${style.fontSize} ${style.fontFamily}`;
    const textWidth = getTextWidth(text, font);
    const padding = 108;
    const minWidth = text.length > 10 ? 153 : 108;
    return Math.max(textWidth + padding, minWidth);
  }, []);

  // const autoResizeTextarea = useCallback((textarea) => {
  //   if (textarea) {
  //     textarea.style.height = 'auto';
  //     textarea.style.height = `${textarea.scrollHeight}px`;
  //     textarea.style.width = '100%';
  //   }
  // }, []);

  const updateInputWidths = useCallback(() => {
    let maxWidth = 0;
    Object.keys(inputRefs.current).forEach(key => {
      if (!['name', 'title', 'email', 'phone', 'location'].includes(key)) {
        const input = inputRefs.current[key];
        if (input) {
          const width = calculateInputWidth(input);
          if (width > maxWidth) {
            maxWidth = width;
          }
        }
      }
    });
    Object.keys(inputRefs.current).forEach(key => {
      if (!['name', 'title', 'email', 'phone', 'location'].includes(key)) {
        const input = inputRefs.current[key];
        if (input) {
          input.style.width = `${maxWidth}px`;
        }
      }
    });
  }, [calculateInputWidth]);

  useEffect(() => {
    updateInputWidths();

    const handleResize = () => updateInputWidths();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [updateInputWidths, name, title, location, email, phone]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.target.blur();
    }
  }, []);

  const handleLocationSelect = useCallback((selectedLocation) => {
    onInputChange({ target: { name: 'location', value: selectedLocation } });
    updateInputWidths();
  }, [onInputChange, updateInputWidths]);

  const handleHeaderClick = useCallback((sectionName) => {
    if (activeSection === sectionName) {
      // Animate close, then clear active section
      setClosingSection(sectionName);
      const duration = 300; // keep in sync with CSS
      // Delay actual close to allow animation to play
      setTimeout(() => {
        if (typeof setActiveSectionProp === 'function') {
          setActiveSectionProp(null);
        } else {
          // Fallback uses parent toggle to null by passing same key, but ensure it's closing
          toggleSection(sectionName);
        }
        setClosingSection(null);
      }, duration);
    } else {
      // Open immediately
      if (typeof setActiveSectionProp === 'function') {
        setActiveSectionProp(sectionName);
      } else {
        toggleSection(sectionName);
      }
    }
  }, [activeSection, toggleSection, setActiveSectionProp]);

  const renderNameAndTitleSection = useCallback(() => (
    <div className={`section ${activeSection === 'nameAndTitle' ? 'active' : ''}`}>
      <div className="section-toggle" onClick={() => handleHeaderClick('nameAndTitle')}>
        <h2>Name & Title {activeSection === 'nameAndTitle' ? '▲' : '▼'}</h2>
      </div>
      <div className={`section-content ${activeSection === 'nameAndTitle' ? 'active' : ''} ${closingSection === 'nameAndTitle' ? 'closing' : ''}`}>
        <div className="input-container">
          <label htmlFor="name">Name</label>
          <ClearableInput
            name="name"
            value={name}
            onChange={onInputChange}
            onFocus={() => handleFocus('name')}
            onBlur={handleBlur}
            placeholder="Enter your name"
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            className="short-field"
          />
        </div>
        <div className="input-container">
          <label htmlFor="title">Title</label>
          <ClearableInput
            name="title"
            value={title}
            onChange={onInputChange}
            onFocus={() => handleFocus('title')}
            onBlur={handleBlur}
            placeholder="Enter your title"
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            className="short-field"
          />
        </div>
        <button onClick={() => onGenerateClick('title')} disabled={isLoading}>
          {isLoading ? <div className="spinner"></div> : 'Generate Title'}
        </button>
      </div>
    </div>
  ), [activeSection, handleHeaderClick, closingSection, name, title, onInputChange, handleFocus, handleBlur, handleKeyDown, isLoading, onGenerateClick]);

  const renderContactSection = useCallback(() => (
    <div className={`section ${activeSection === 'contact' ? 'active' : ''}`}>
      <div className="section-toggle" onClick={() => handleHeaderClick('contact')}>
        <h2>Contact {activeSection === 'contact' ? '▲' : '▼'}</h2>
      </div>
      <div className={`section-content ${activeSection === 'contact' ? 'active' : ''} ${closingSection === 'contact' ? 'closing' : ''}`}>
        <div className="input-container">
          <label htmlFor="email">Email</label>
          <ClearableInput
            name="email"
            value={email}
            onChange={onInputChange}
            onFocus={() => handleFocus('email')}
            onBlur={handleBlur}
            placeholder="Enter your email"
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            className="short-field"
          />
        </div>
        <div className="input-container">
          <label htmlFor="phone">Phone</label>
          <PhoneNumberInput
            value={phone}
            onChange={(value) => onInputChange({ target: { name: 'phone', value } })}
            disabled={isLoading}
            className="short-field"
          />
        </div>
        <div className="input-container">
          <label htmlFor="location">Location</label>
          <LocationAutocomplete
            value={location}
            onChange={(value) => onInputChange({ target: { name: 'location', value } })}
            onSelect={handleLocationSelect}
            disabled={isLoading}
            className="short-field"
            onClear={() => onInputChange({ target: { name: 'location', value: '' } })}
          />
        </div>
      </div>
    </div>
  ), [activeSection, handleHeaderClick, closingSection, email, phone, location, onInputChange, handleFocus, handleBlur, handleKeyDown, isLoading, handleLocationSelect]);

  const renderSection = useCallback((sectionName, label, value, placeholder, isMultiLine = false, showGenerateButton = false) => {
    const handleSectionKeyDown = (e) => {
      if (!isMultiLine) {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.target.blur();
        }
        return;
      }

      if (e.key === 'Enter') {
        if (e.shiftKey) {
          return;
        }
        return;
      }
    };

    return (
      <div className={`section ${activeSection === sectionName ? 'active' : ''}`}>
        <div className="section-toggle" onClick={() => handleHeaderClick(sectionName)}>
          <h2>{label} {activeSection === sectionName ? '▲' : '▼'}</h2>
        </div>
        <div
          className={`section-content ${activeSection === sectionName ? 'active' : ''} ${closingSection === sectionName ? 'closing' : ''}`}
          ref={(el) => (sectionRefs.current[sectionName] = el)}
        >
          <div className="input-container">
            <div className="label-with-mic">
              <label htmlFor={sectionName}>{label}</label>
              {showGenerateButton && (
                <button onClick={() => onGenerateClick(sectionName)} disabled={isLoading}>
                  {isLoading ? <div className="spinner"></div> : `Generate ${label}`}
                </button>
              )}
            </div>
            <ClearableInput
              ref={(el) => (inputRefs.current[sectionName] = el)}
              name={sectionName}
              value={value}
              onChange={onInputChange}
              placeholder={placeholder}
              isMultiLine={isMultiLine}
              onKeyDown={handleSectionKeyDown}
              onFocus={() => handleFocus(sectionName)}
              onBlur={handleBlur}
              disabled={isLoading}
              className={isMultiLine ? "long-input auto-resize" : ""}
            />
            <CharacterCount current={Array.isArray(value) ? value.join('').length : value.length} max={characterLimits[sectionName]} />
            {statusMessage && <div className="status-message">{statusMessage}</div>}
          </div>
        </div>
      </div>
    );
  }, [activeSection, handleHeaderClick, closingSection, activeField, onInputChange, isLoading, onGenerateClick, statusMessage, characterLimits, handleFocus, handleBlur]);

  useEffect(() => {
    // When a section opens, scroll the panel to that section and auto-resize textareas
    if (activeSection && sectionRefs.current[activeSection]) {
      const panel = panelRef.current;
      const target = sectionRefs.current[activeSection];
      if (panel && target) {
        const top = Math.max(target.offsetTop - 8, 0);
        try { panel.scrollTo({ top, behavior: 'smooth' }); } catch { panel.scrollTop = top; }
      }
      const timer = setTimeout(() => {
        const panel2 = panelRef.current;
        const container = sectionRefs.current[activeSection];
        if (panel2 && container) {
          // Scroll again post-expansion to ensure no manual scroll is required
          const top2 = Math.max(container.offsetTop - 8, 0);
          try { panel2.scrollTo({ top: top2, behavior: 'smooth' }); } catch { panel2.scrollTop = top2; }
          const textareas = container.querySelectorAll('textarea');
          textareas.forEach((ta) => {
            ta.style.height = 'auto';
            ta.style.height = `${ta.scrollHeight}px`;
          });
        }
      }, 380);
      return () => clearTimeout(timer);
    }
  }, [activeSection]);

  // Side tabs list
  const tabs = [
    { key: 'nameAndTitle', label: 'Name & Title' },
    { key: 'contact', label: 'Contact' },
    { key: 'summary', label: 'Summary' },
    { key: 'experience', label: 'Experience' },
    { key: 'skills', label: 'Skills' },
    { key: 'education', label: 'Education' },
    { key: 'publications', label: 'Publications' },
    { key: 'coverLetter', label: 'Cover Letter' },
  ];

  const setActive = (key) => {
    if (activeSection === key) {
      // same as header click close
      handleHeaderClick(key);
      return;
    }
    if (typeof setActiveSectionProp === 'function') {
      setActiveSectionProp(key);
    } else {
      toggleSection(key);
    }
  };

  const isDocked = !!activeSection;
  return (
    <div className={`desktop-edit-panel ${isDocked ? 'side-tabs-layout docked' : 'undocked'}`}>
      {isDocked && (
        <nav className="side-tabs" aria-label="Editor sections">
          {tabs.map((t) => (
            <button
              key={t.key}
              className={`side-tab ${activeSection === t.key ? 'active' : ''}`}
              onClick={() => setActive(t.key)}
              type="button"
            >
              {t.label}
            </button>
          ))}
        </nav>
      )}
      <div className="content-pane" ref={panelRef}>
        <div className="import-resume-container">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept=".pdf,.txt"
            style={{ display: 'none' }}
          />
          <button
            type="button"
            className="import-resume-btn"
            onClick={handleImportClick}
            disabled={isImporting}
          >
            {isImporting ? (
              <>
                <div className="spinner" style={{ marginRight: '8px' }}></div>
                <span>Parsing resume...</span>
              </>
            ) : (
              <>
                <FaCloudUploadAlt style={{ fontSize: '1.2rem', marginRight: '8px' }} />
                <span>Import Existing Resume (PDF / TXT)</span>
              </>
            )}
          </button>
        </div>
        {renderNameAndTitleSection()}
        {renderContactSection()}
        {renderSection('summary', 'Summary', summary, 'Enter your summary', true, true)}
        {renderSection('experience', 'Experience', experience, 'Enter your experience', true, true)}
        {renderSection('skills', 'Skills', skills, 'Enter your skills', true, true)}
        {renderSection('education', 'Education', education, 'Enter your education', true, true)}
        {renderSection('publications', 'Publications', publications, 'Enter your publications', true, true)}
        <div
          className={`section ${activeSection === 'coverLetter' ? 'active' : ''}`}
          ref={(el) => (sectionRefs.current['coverLetter'] = el)}
        >
          <div className="section-toggle" onClick={() => handleHeaderClick('coverLetter')}>
            <h2>Cover Letter {activeSection === 'coverLetter' ? '▲' : '▼'}</h2>
          </div>
          <div className={`section-content ${activeSection === 'coverLetter' ? 'active' : ''} ${closingSection === 'coverLetter' ? 'closing' : ''}`}>
            {hasCoverLetterAccess ? (
              <>
                <CoverLetterGen
                  formData={{
                    jobDescription,
                    jobPostUrl,
                    companyName,
                    targetRole,
                    applicantAddress,
                    recipientName,
                    recipientJobTitle,
                    coverLetterBody,
                    coverLetter,
                    whyCompany,
                    keyAchievements,
                    keyRoleSkills,
                  }}
                  handleInputChange={onInputChange}
                  handleCoverLetterGenerate={handleCoverLetterGenerate}
                  isLoading={isLoading}
                />
                {/* Show job summary preview only until a cover letter exists */}
                {!coverLetter && jobPreview && (jobPreview.summary || (Array.isArray(jobPreview.responsibilities)&&jobPreview.responsibilities.length) || (Array.isArray(jobPreview.requirements)&&jobPreview.requirements.length)) && (
                  <div className="job-preview" style={{ border: '1px solid var(--border-color, #ddd)', borderRadius: 6, padding: 12, marginTop: 12 }}>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Job summary preview</div>
                    {jobPreview.summary && (
                      <div style={{ marginBottom: 8 }}>{jobPreview.summary}</div>
                    )}
                    {Array.isArray(jobPreview.responsibilities) && jobPreview.responsibilities.length > 0 && (
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ fontWeight: 500 }}>Responsibilities</div>
                        <ul style={{ margin: '4px 0 0 18px' }}>
                          {jobPreview.responsibilities.slice(0, 5).map((r, i) => (
                            <li key={`resp-${i}`}>{String(r)}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {Array.isArray(jobPreview.requirements) && jobPreview.requirements.length > 0 && (
                      <div>
                        <div style={{ fontWeight: 500 }}>Requirements</div>
                        <ul style={{ margin: '4px 0 0 18px' }}>
                          {jobPreview.requirements.slice(0, 5).map((r, i) => (
                            <li key={`req-${i}`}>{String(r)}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
                {/* Cover letter preview above the editor */}
                {coverLetter && String(coverLetter).trim() && (
                  <div className="cover-letter-preview">
                    <div className="preview-header">Cover letter preview</div>
                    <div className="preview-body">
                      {String(coverLetter)
                        .split(/\n\n+/)
                        .map((para, idx) => (
                          <p key={`clp-${idx}`}>{para.split(/\n/).map((line, i) => (
                            <React.Fragment key={`line-${i}`}>{line}{i < para.split(/\n/).length - 1 ? <br /> : null}</React.Fragment>
                          ))}</p>
                        ))}
                    </div>
                  </div>
                )}
                <div className="input-container" style={{ marginTop: '1rem' }}>
                  <label htmlFor="coverLetter">Cover Letter (editable)</label>
                  <ClearableInput
                    ref={(el) => (inputRefs.current['coverLetter'] = el)}
                    name="coverLetter"
                    value={coverLetter}
                    onChange={onInputChange}
                    placeholder="Your cover letter content will appear here and can be edited."
                    isMultiLine={true}
                    onFocus={() => handleFocus('coverLetter')}
                    onBlur={handleBlur}
                    disabled={isLoading}
                    className="long-input auto-resize"
                  />
                  <CharacterCount current={Array.isArray(coverLetter) ? coverLetter.join('').length : coverLetter.length} max={characterLimits['coverLetter']} />
                  {statusMessage && <div className="status-message">{statusMessage}</div>}
                </div>
              </>
            ) : (
              <div className="premium-lock-gate">
                <div className="lock-icon">🔒</div>
                <h3>Unlock AI Cover Letter Generator</h3>
                <p className="description">
                  Tailor high-converting, professional cover letters matched perfectly to your resume and any job description in seconds.
                </p>
                <ul className="benefits-list">
                  <li>
                    <span className="benefit-icon">✨</span>
                    <span><strong>Instant Tailoring:</strong> Automatically align your experience with job requirements.</span>
                  </li>
                  <li>
                    <span className="benefit-icon">✍️</span>
                    <span><strong>Professional Copy:</strong> Write with the perfect tone and vocabulary for target employers.</span>
                  </li>
                  <li>
                    <span className="benefit-icon">🚀</span>
                    <span><strong>Stand Out:</strong> Candidates who submit tailored cover letters get up to 40% more interview requests.</span>
                  </li>
                </ul>
                <button 
                  className="premium-upgrade-btn" 
                  onClick={() => navigate('/pricing')}
                >
                  Upgrade to Professional / Ultimate
                </button>
              </div>
            )}
          </div>
        </div>
        {errorMessage && (
          <Popup
            message={errorMessage}
            onClose={() => setErrorMessage('')}
          />
        )}
      </div>
    </div>
  );
};

export default EditPanel;